import BrowseLines from './BrowseLines'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Input, Modal, Select, Spinner } from '../ui'
import { concentrationPresentation, resolvedConcentrationDisplay } from '../../lib/concentrationResolver'
import { toOwnershipPayload } from '../../lib/collectionAcquisition'
import {
  buildHouseHierarchy,
  fetchCatalogHouses,
  fetchCompleteHouseCatalog,
  groupLinesForBrowse,
  MIN_HOUSE_QUERY_LENGTH,
} from '../../lib/houseHierarchy'
import { supabase } from '../../lib/supabase'
import { CatalogBottlePlaceholder, ConcentrationPill } from '../add/CatalogResultVisuals'
import { usableCardImage } from '../../lib/catalogSearchHouseFirst'

const SIZE_OPTIONS = ['', '5', '10', '30', '50', '60', '75', '100', '125', '150', '200']

export function BottleThumbnail({ row, size = 'bottle' }) {
  const image = usableCardImage(row)
  const [failed, setFailed] = useState(false)
  useEffect(() => { setFailed(false) }, [image])
  const showImage = Boolean(image) && !failed
  const sizeClass = size === 'line' ? 'w-11' : 'w-12'
  return (
    <div
      className={`block aspect-[4/5] ${sizeClass} shrink-0 overflow-hidden rounded-lg bg-charcoal-700`}
      data-thumbnail-for={row?.id || ''}
      data-thumbnail-state={showImage ? 'image' : 'placeholder'}
    >
      {showImage
        ? <img src={image} alt="" className="h-full w-full bg-white object-contain p-1" loading="lazy" data-bottle-image={size === 'bottle' ? 'true' : undefined} onError={() => setFailed(true)} />
        : <CatalogBottlePlaceholder className="h-full w-full" />}
    </div>
  )
}

export default function HierarchyMultiAddModal({ open, onClose, onSearchInstead, onPhotoInstead, onVoiceInstead, initialHouseQuery = '', addFragrances, findDuplicate }) {
  const [houseQuery, setHouseQuery] = useState('')
  const [houses, setHouses] = useState([])
  const [houseRows, setHouseRows] = useState([])
  const [activeHouse, setActiveHouse] = useState('')
  const [activeLineName, setActiveLineName] = useState('')
  const [lineQuery, setLineQuery] = useState('')
  const [showLineFilter, setShowLineFilter] = useState(false)
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [bottleMl, setBottleMl] = useState('')
  const [loadingHouses, setLoadingHouses] = useState(false)
  const [loadingHouse, setLoadingHouse] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const houseRequest = useRef(0)

  const hierarchy = useMemo(() => buildHouseHierarchy(houseRows)[0] || null, [houseRows])
  const lines = useMemo(() => {
    const q = lineQuery.trim().toLocaleLowerCase()
    if (!q) return hierarchy?.lines || []
    return (hierarchy?.lines || []).filter(line => (
      line.name.toLocaleLowerCase().includes(q)
      || line.variants.some(variant => variant.name.toLocaleLowerCase().includes(q))
    ))
  }, [hierarchy, lineQuery])
  const activeLine = useMemo(
    () => hierarchy?.lines.find(line => line.name === activeLineName) || null,
    [hierarchy, activeLineName],
  )
  const lineGroups = useMemo(
    () => groupLinesForBrowse(lines, hierarchy?.lines.length || 0),
    [hierarchy, lines],
  )

  useEffect(() => {
    if (!open || !initialHouseQuery) return
    setHouseQuery(initialHouseQuery)
  }, [open, initialHouseQuery])

  useEffect(() => {
    if (!open) return undefined
    const q = houseQuery.trim()
    const requestId = ++houseRequest.current
    if (q.length < MIN_HOUSE_QUERY_LENGTH) {
      setHouses([])
      setLoadingHouses(false)
      setError('')
      return undefined
    }
    setLoadingHouses(true)
    setError('')
    const timer = window.setTimeout(async () => {
      try {
        const candidates = await fetchCatalogHouses(supabase, q)
        if (requestId === houseRequest.current) setHouses(candidates)
      } catch (loadError) {
        if (requestId === houseRequest.current) {
          setHouses([])
          setError(loadError?.message || 'Could not search houses.')
        }
      } finally {
        if (requestId === houseRequest.current) setLoadingHouses(false)
      }
    }, 250)
    return () => window.clearTimeout(timer)
  }, [open, houseQuery])

  const reset = () => {
    houseRequest.current += 1
    setHouseQuery('')
    setHouses([])
    setHouseRows([])
    setActiveHouse('')
    setActiveLineName('')
    setLineQuery('')
    setShowLineFilter(false)
    setSelectedIds(new Set())
    setBottleMl('')
    setLoadingHouses(false)
    setLoadingHouse(false)
    setSaving(false)
    setError('')
  }

  const close = () => {
    reset()
    onClose()
  }

  // Carry whatever was typed into the catalog search so nobody retypes it.
  const searchInstead = () => {
    const typed = houseQuery.trim()
    reset()
    onSearchInstead?.(typed)
  }

  const photoInstead = () => {
    reset()
    onPhotoInstead?.()
  }

  const voiceInstead = () => {
    reset()
    onVoiceInstead?.()
  }

  const chooseHouse = async candidate => {
    setLoadingHouse(true)
    setError('')
    try {
      const rows = await fetchCompleteHouseCatalog(supabase, candidate.house)
      setHouseRows(rows)
      setActiveHouse(candidate.house)
      setActiveLineName('')
      setLineQuery('')
      setShowLineFilter(false)
      setSelectedIds(new Set())
    } catch (loadError) {
      setError(loadError?.message || 'Could not load this house.')
    } finally {
      setLoadingHouse(false)
    }
  }

  const presentationFor = variant => concentrationPresentation(variant)
  const isSelectable = variant => {
    const presentation = presentationFor(variant)
    return presentation.status.startsWith('resolved') && Boolean(presentation.display)
  }
  const alreadyOwned = variant => typeof findDuplicate === 'function' && findDuplicate({
    name: variant.name,
    house: variant.house,
    concentration: resolvedConcentrationDisplay(variant),
  })

  const toggleVariant = variant => {
    if (!isSelectable(variant) || alreadyOwned(variant)) return
    setSelectedIds(current => {
      const next = new Set(current)
      if (next.has(variant.id)) next.delete(variant.id)
      else next.add(variant.id)
      return next
    })
  }

  const leaveHouse = () => {
    setActiveHouse('')
    setHouseRows([])
    setActiveLineName('')
    setLineQuery('')
    setShowLineFilter(false)
    setSelectedIds(new Set())
  }

  const toggleLine = lineName => {
    setActiveLineName(current => current === lineName ? '' : lineName)
  }

  const selectedRows = useMemo(
    () => houseRows.filter(row => selectedIds.has(row.id)),
    [houseRows, selectedIds],
  )

  const commit = async () => {
    setSaving(true)
    setError('')
    try {
      const payloads = selectedRows.map(row => toOwnershipPayload({
        ...row,
        catalog_id: row.id,
        concentration: resolvedConcentrationDisplay(row),
      }, bottleMl))
      await addFragrances(payloads)
      close()
    } catch (saveError) {
      setError(saveError?.message || 'Could not add the selected bottles.')
      setSaving(false)
    }
  }

  return (
    <Modal updateEntry
      open={open}
      onClose={close}
      title="Browse fragrances"
      maxWidth="max-w-2xl"
      fullScreenMobile
      bodyClassName="flex flex-col !overflow-hidden sm:block sm:!overflow-visible"
    >
      <div className="flex min-h-0 flex-1 flex-col gap-3" data-browse-level={activeHouse ? 'lines' : 'houses'}>
        {!activeHouse ? (
          <>
            <div className="shrink-0">
              <p className="text-sm text-cream/70">Choose a house, choose a line, then tick every bottle you own.</p>
              <button type="button" onClick={searchInstead} className="mt-1 text-xs text-gold underline underline-offset-2 hover:text-gold-light">
                Search the catalog instead
              </button>
              {!houseQuery.trim() && (
                <button type="button" onClick={photoInstead} className="ml-4 text-xs font-semibold text-gold underline underline-offset-2 hover:text-gold-light">
                  Add from photo
                </button>
              )}
              {onVoiceInstead && !houseQuery.trim() && (
                <button type="button" onClick={voiceInstead} data-add-by-voice className="ml-4 text-xs font-semibold text-gold underline underline-offset-2 hover:text-gold-light">
                  Add by voice
                </button>
              )}
            </div>
            <div className="relative shrink-0">
              <Input
                label="House"
                value={houseQuery}
                onChange={event => setHouseQuery(event.target.value)}
                placeholder="Search houses"
                autoComplete="off"
                autoFocus
              />
              {(loadingHouses || loadingHouse) && <span className="absolute right-3 top-8"><Spinner size="sm" /></span>}
            </div>
            {error && <p className="shrink-0 text-xs text-red-400" role="alert">{error}</p>}
            <div className="min-h-0 flex-1 overflow-y-auto rounded-xl border border-white/10" data-visual-list="browse-house-results">
              {houses.map(candidate => (
                <button
                  key={candidate.house}
                  data-list-key={candidate.house.toLocaleLowerCase()}
                  type="button"
                  disabled={loadingHouse}
                  onClick={() => chooseHouse(candidate)}
                  className="flex min-h-14 w-full items-center justify-between gap-3 border-b border-white/5 px-3 py-2 text-left transition-colors last:border-b-0 hover:bg-white/5 focus:bg-white/5 focus:outline-none disabled:opacity-40"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-base text-cream">{candidate.house}</span>
                    <span className="text-[11px] text-cream/40">{candidate.row_count} catalog bottle{candidate.row_count === 1 ? '' : 's'}</span>
                  </span>
                  <span className="text-gold/70" aria-hidden>→</span>
                </button>
              ))}
              {!loadingHouses && houseQuery.trim().length < MIN_HOUSE_QUERY_LENGTH && (
                <p className="px-4 py-10 text-center text-sm text-cream/40">Type at least three letters to browse wearable perfume houses.</p>
              )}
              {!loadingHouses && houseQuery.trim().length >= MIN_HOUSE_QUERY_LENGTH && houses.length === 0 && !error && (
                <div className="px-4 py-10 text-center" data-browse-empty="no-house">
                  <p className="text-sm text-cream/60">No house is called that — it may be a fragrance name.</p>
                  <button
                    type="button"
                    onClick={searchInstead}
                    className="mt-3 rounded-full border border-gold/40 px-4 py-2 text-sm font-semibold text-gold hover:bg-gold/10"
                    data-browse-search-typed
                  >
                    Search every fragrance for “{houseQuery.trim()}”
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="flex shrink-0 items-center justify-between gap-3 rounded-xl border border-gold/20 bg-gold/5 p-3">
              <div>
                <button type="button" onClick={leaveHouse} className="text-[11px] text-gold hover:text-gold-light">
                  ← Houses
                </button>
                <h3 className="mt-1 text-xl text-cream">{activeHouse}</h3>
                <p className="text-xs text-cream/40">{hierarchy?.lines.length || 0} lines · {hierarchy?.row_count || 0} wearable catalog bottles</p>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto rounded-xl border border-white/10" data-visual-list="browse-line-results">
              <BrowseLines lineGroups={lineGroups} activeLineName={activeLineName} activeHouse={activeHouse}
                toggleLine={toggleLine} alreadyOwned={alreadyOwned}
                renderBottle={variant => {
                  const presentation = presentationFor(variant)
                  const owned = Boolean(alreadyOwned(variant))
                  const selectable = isSelectable(variant) && !owned
                  const checked = selectedIds.has(variant.id)
                  return (
                    <label
                      key={variant.id}
                      data-list-key={variant.id}
                      data-bottle-row
                      data-owned={owned ? 'true' : 'false'}
                      className={`flex min-h-[4.75rem] items-center gap-3 border-b border-white/5 px-3 py-2 pl-5 last:border-b-0 ${selectable ? 'cursor-pointer hover:bg-white/[0.04]' : 'cursor-not-allowed opacity-55'}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!selectable || saving}
                        onChange={() => toggleVariant(variant)}
                        className="h-5 w-5 shrink-0 accent-[#D4AF37]"
                      />
                                  <BottleThumbnail row={variant} />
                                  <span className="min-w-0 flex-1">
                                    <span className="block text-sm text-cream">{variant.name}</span>
                                    <span className={`text-[10px] ${owned ? 'font-semibold text-sage' : 'text-cream/40'}`}>
                                      {owned ? 'Owned · already on your shelf' : !isSelectable(variant) ? 'Concentration needs verification' : variant.year || 'Year not recorded'}
                                    </span>
                                  </span>
                                  <ConcentrationPill concentration={presentation.display} />
                                </label>
                              )
                            }} />
              {lines.length === 0 && <p className="px-4 py-10 text-center text-sm text-cream/40">No lines match that filter.</p>}
            </div>
            <div className="shrink-0 border-t border-white/10 pt-2" data-line-filter-controls>
              <button
                type="button"
                aria-expanded={showLineFilter}
                onClick={() => setShowLineFilter(current => !current)}
                className="text-xs text-cream/50 underline decoration-white/20 underline-offset-2 hover:text-gold"
              >
                {showLineFilter ? 'Hide filter' : 'Filter'}
              </button>
              {showLineFilter && (
                <div className="mt-2">
                  <Input
                    label="Filter lines"
                    value={lineQuery}
                    onChange={event => {
                      setLineQuery(event.target.value)
                      setActiveLineName('')
                    }}
                    placeholder="Filter fragrance lines"
                  />
                </div>
              )}
            </div>
            {selectedRows.length > 0 && <details className="shrink-0 rounded-lg border border-gold/20 p-2" open>
              <summary className="text-sm text-gold">{selectedRows.length} selected across {activeHouse}</summary>
              <ul className="max-h-28 overflow-y-auto text-xs text-cream/70">{selectedRows.map(row => <li key={row.id} className="flex items-center justify-between gap-2">
                <span>{row.name} · {resolvedConcentrationDisplay(row)} · {row.year || 'Year not recorded'}</span>
                <button type="button" className="min-h-11 px-2 text-gold" disabled={saving} aria-label={`Remove ${row.name} from selection`} onClick={() => toggleVariant(row)}>Remove</button>
              </li>)}</ul>
              <p className="text-xs text-cream/50">Selections stay while you browse other lines in this house.</p>
            </details>}
            {(activeLine || selectedRows.length > 0) && (
              <div className="grid shrink-0 grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)] gap-3 border-t border-white/10 bg-charcoal-800 pt-3" data-browse-actions>
                <Select
                  label="Size for selected"
                  aria-label="Size for selected"
                  value={bottleMl}
                  disabled={saving}
                  onChange={event => setBottleMl(event.target.value)}
                  options={SIZE_OPTIONS.map(value => ({ value, label: value ? `${value} ml` : 'Not sure' }))}
                />
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium uppercase tracking-wide text-cream/60">Review selection</span>
                  <Button type="button" disabled={selectedIds.size === 0 || saving} onClick={commit} className="w-full">
                    {saving ? <><Spinner size="sm" /> Adding…</> : `Add ${selectedIds.size || ''} bottle${selectedIds.size === 1 ? '' : 's'}`}
                  </Button>
                </div>
              </div>
            )}
            {error && <p className="shrink-0 text-xs text-red-400" role="alert">{error}</p>}
          </>
        )}
      </div>
    </Modal>
  )
}
